import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldAlert, Plus, Pencil, Check, Thermometer } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useFirma } from "../context/FirmaContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { EVENTO_TIPI, eventoColor } from "../lib/eventiSanitari";
import { NoteDiario, fmtDataLunga } from "../pages/Infettive";
import { ArchivioEventiButton } from "./ArchivioEventi";
import { EventiRisoltiBox } from "./EventiRisoltiBox";
import { VisiteDaTogliere, togliVisiteEvento } from "./VisiteEvento";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const TIPO_LBL = Object.fromEntries(EVENTO_TIPI);
const tipoTxt = (e) => (e?.tipo === "altro" && (e.tipo_altro || "").trim()) ? e.tipo_altro.trim() : (TIPO_LBL[e?.tipo] || "Evento sanitario");
const giorniDa = (da) => { if (!da) return null; const n = Math.round((new Date(ymd(new Date()) + "T00:00:00") - new Date(da + "T00:00:00")) / 86400000) + 1; return n > 0 ? n : null; };
const INVALIDA = ["infettive-febbre-stato", "inf-episodi", "infettive-day", "infettive-mese", "infettive", "infettive-oggi", "nav-badges", "inf-temp", "inf-modifiche"];

const F = ({ label, children }) => <div><Label className="text-xs font-semibold mb-1 block">{label}</Label>{children}</div>;

function SegnalazioneDialog({ dlg, setDlg, onSave, busy }) {
  const set = (k, v) => setDlg((p) => ({ ...p, [k]: v }));
  return (
    <Dialog open={!!dlg} onOpenChange={(o) => { if (!o) setDlg(null); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="scheda-es-dialog">
        <DialogHeader>
          <DialogTitle>{dlg?.edit ? "Modifica evento sanitario" : "Segnalazione sanitaria"}</DialogTitle>
          <DialogDescription>{dlg?.edit ? `Evento iniziato ${fmtDataLunga(dlg.data)}.` : "Apre un nuovo evento sanitario per questo ospite."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">{EVENTO_TIPI.map(([k, l]) => { const c = eventoColor(k); const on = dlg?.tipo === k; return (
            <button key={k} type="button" onClick={() => set("tipo", k)} data-testid={`scheda-es-tipo-${k}`} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors" style={on ? { background: c.bg, color: c.fg, borderColor: c.bg } : { background: "#fff" }}>{l}</button>); })}</div>
          {dlg?.tipo === "altro" && <F label="Tipo di evento *"><Input value={dlg?.tipo_altro || ""} onChange={(e) => set("tipo_altro", e.target.value)} data-testid="scheda-es-tipo-altro" /></F>}
          <F label="Temperatura °C"><Input type="number" step="0.1" value={dlg?.temperatura ?? ""} onChange={(e) => set("temperatura", e.target.value)} data-testid="scheda-es-temp" /></F>
          <F label="Sintomi *"><Textarea rows={3} value={dlg?.sintomi || ""} onChange={(e) => set("sintomi", e.target.value)} placeholder="Un sintomo per riga" data-testid="scheda-es-sintomi" /></F>
          <F label="Provvedimenti adottati"><Textarea rows={2} value={dlg?.provvedimenti || ""} onChange={(e) => set("provvedimenti", e.target.value)} data-testid="scheda-es-provvedimenti" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Medico"><Input value={dlg?.medico || ""} onChange={(e) => set("medico", e.target.value)} data-testid="scheda-es-medico" /></F>
            <F label="Recapito medico"><Input value={dlg?.medico_tel || ""} onChange={(e) => set("medico_tel", e.target.value)} data-testid="scheda-es-medico-tel" /></F>
          </div>
          <F label="Note"><Textarea rows={2} value={dlg?.note || ""} onChange={(e) => set("note", e.target.value)} data-testid="scheda-es-note" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button>
          <Button onClick={onSave} disabled={busy} data-testid="scheda-es-salva"><Check size={15} className="mr-1" /> Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RisolviDialog({ ris, setRis, onConfirm, busy, nome, ospiteId }) {
  return (
    <Dialog open={!!ris} onOpenChange={(o) => { if (!o) setRis(null); }}>
      <DialogContent data-testid="scheda-es-risolvi-dialog">
        <DialogHeader><DialogTitle>Evento risolto — {nome}</DialogTitle><DialogDescription>L'evento verrà chiuso e resterà consultabile tra gli eventi risolti.</DialogDescription></DialogHeader>
        <F label="Note di risoluzione (facoltative)"><Textarea rows={3} value={ris?.note || ""} onChange={(e) => setRis((p) => ({ ...p, note: e.target.value }))} placeholder="Es. sfebbrato da 24h, medico avvisato..." data-testid="scheda-es-risolvi-note" /></F>
        <VisiteDaTogliere ospiteId={ris ? ospiteId : null} scelte={ris?.scelte || {}} setScelta={(id, s) => setRis((p) => ({ ...p, scelte: { ...(p.scelte || {}), [id]: s } }))} />
        <DialogFooter><Button variant="outline" onClick={() => setRis(null)}>Annulla</Button><Button className="bg-emerald-600 hover:bg-emerald-700" onClick={onConfirm} disabled={busy} data-testid="scheda-es-risolvi-conferma"><Check size={15} className="mr-1" /> Conferma risolto</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EventoInCorso({ ospite, st, onEdit, onRisolvi }) {
  const col = eventoColor(st.tipo);
  const g = giorniDa(st.da);
  const caso = { tipo: st.tipo, tipo_altro: st.tipo_altro, temperatura: st.temperatura, sintomi: st.sintomi, provvedimenti: st.provvedimenti, note: st.note, operatore: st.operatore, modificato_da: st.modificato_da, medico: st.medico, medico_tel: st.medico_tel, responsabile: st.responsabile, da: st.da };
  return (
    <div className="gr-card p-4 border-l-4" style={{ borderLeftColor: col.bg }} data-testid="scheda-es-in-corso">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-block w-2 h-2 rounded-full animate-pulse" style={{ background: col.bg }} />
        <span className="font-head font-bold" style={{ color: col.txt }}>{tipoTxt(st)} in corso</span>
        <span className="text-xs text-muted-foreground">da {fmtDataLunga(st.da)}{g ? ` · ${g} ${g === 1 ? "giorno" : "giorni"}` : ""}</span>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => onEdit(caso)} data-testid="scheda-es-modifica"><Pencil size={14} className="mr-1" /> Modifica</Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={onRisolvi} data-testid="scheda-es-risolvi"><Check size={14} className="mr-1" /> Risolvi</Button>
        </div>
      </div>
      <NoteDiario ospiteId={ospite.id} structureId={ospite.structure_id} color="#475569" caso={caso} data={ymd(new Date())} onEdit={() => onEdit(caso)}
        medicoFallback={{ medico: ospite.medico_base, medico_tel: ospite.telefono_medico, responsabile: ospite.responsabile_nome }} />
    </div>
  );
}

export function EventiSanitariOspite({ ospite }) {
  const qc = useQueryClient();
  const { canSign, signerName } = useFirma();
  const [dlg, setDlg] = useState(null);
  const [ris, setRis] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data: st } = useQuery({ queryKey: ["infettive-febbre-stato", ospite.id], queryFn: async () => (await api.get("/infettive/febbre-stato", { params: { ospite_id: ospite.id } })).data, refetchInterval: 30000 });
  const refresh = () => INVALIDA.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const needSign = () => { if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN)"); return true; } return false; };
  const apriNuova = () => { if (needSign()) return; setDlg({ data: ymd(new Date()), tipo: "febbre", temperatura: "", sintomi: "", provvedimenti: "", medico: ospite.medico_base || "", medico_tel: ospite.telefono_medico || "", note: "" }); };
  const apriModifica = (c) => { if (needSign()) return; setDlg({ ...c, data: c.da, temperatura: c.temperatura ?? "", edit: true }); };
  const salva = async () => {
    const c = dlg;
    if (c.tipo === "altro" && !(c.tipo_altro || "").trim()) { toast.error("Scrivi il tipo di evento sanitario"); return; }
    if (!(c.sintomi || "").trim()) { toast.error("Compila i Sintomi (obbligatorio)"); return; }
    setBusy(true);
    try {
      await api.post("/infettive/register", { ospite_id: ospite.id, structure_id: ospite.structure_id, data: c.data, stato: "si", tipo: c.tipo, tipo_altro: c.tipo === "altro" ? c.tipo_altro.trim() : "", temperatura: c.temperatura === "" ? null : Number(c.temperatura), sintomi: c.sintomi, provvedimenti: c.provvedimenti || "", medico: (c.medico || "").trim(), medico_tel: (c.medico_tel || "").trim(), responsabile: c.responsabile || "", note: c.note || "", operatore: signerName });
      toast.success(c.edit ? "Evento aggiornato" : "Evento sanitario aperto");
      setDlg(null); refresh();
    } catch { toast.error("Errore nel salvataggio"); }
    setBusy(false);
  };
  const risolvi = async () => {
    setBusy(true);
    try {
      if (!(await togliVisiteEvento(ospite.id, ris?.scelte, signerName))) { setBusy(false); return; }
      await api.post("/infettive/febbre-risolta", { ospite_id: ospite.id, structure_id: ospite.structure_id, note: ris?.note || "", operatore: signerName || undefined });
      toast.success("Evento segnato come risolto"); setRis(null); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };
  return (
    <div className="space-y-4" data-testid="scheda-infettive">
      <div className="gr-card p-4 flex flex-wrap items-center gap-2">
        <span className="font-head font-bold flex items-center gap-2"><ShieldAlert size={16} className="text-red-600" /> Eventi sanitari</span>
        <span className="text-xs text-muted-foreground">{st?.attiva ? "1 evento in corso" : "Nessun evento in corso"}</span>
        <div className="ml-auto flex items-center gap-2">
          <ArchivioEventiButton ospiteId={ospite.id} />
          {!st?.attiva && <Button size="sm" className="h-9" onClick={apriNuova} data-testid="scheda-es-nuova"><Plus size={15} className="mr-1" /> Segnalazione sanitaria</Button>}
        </div>
      </div>
      {st?.attiva ? <EventoInCorso ospite={ospite} st={st} onEdit={apriModifica} onRisolvi={() => { if (!needSign()) setRis({ note: "" }); }} />
        : <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground flex flex-col items-center gap-1" data-testid="scheda-es-nessuno"><Thermometer size={20} className="opacity-40" /> Nessun evento sanitario in corso.</div>}
      <EventiRisoltiBox ospiteId={ospite.id} />
      <SegnalazioneDialog dlg={dlg} setDlg={setDlg} onSave={salva} busy={busy} />
      <RisolviDialog ris={ris} setRis={setRis} onConfirm={risolvi} busy={busy} nome={`${ospite.cognome} ${ospite.nome}`} ospiteId={ospite.id} />
    </div>
  );
}
